import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { environment } from 'environments/environment';
import {
  GetCampaignSupport, GetCampaignSupportGQL, GetOrganizationDetailsGQL, OrganizationDetailsFragment
} from 'generated/graphql';
//import { SupportDialogComponent } from 'app/main/components/support-dialog/support-dialog.component';
import { frontendVersion } from 'generated/version';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { AppPersistantStateService, StoreNames } from 'core/state/app-state.service';
import { WindowRef } from 'core/window-ref.service';
import { isNilOrEmpty } from 'helpers';

type SupportUserType = GetCampaignSupport.SupportUser | OrganizationDetailsFragment['administratorUser'];

@Component({
  selector: 'app-support-dialog-component',
  templateUrl: './support-dialog.component.html',
})
export class SupportDialogComponent extends WithDisposableSubject() implements OnInit, OnDestroy {
  version = frontendVersion;

  isDev = !environment.production;

  /** User displayed in support */
  supportUser: SupportUserType;

  /** Used by view to determine if a campaign is selected */
  isInCampaign = false;

  campaignId$ = this._route.queryParams.pipe(
    takeUntil(this.disposer),
    map(params => params.campaignId),
    tap((campaignId) => {
      // Not currently in a campaign.
      this.isInCampaign = !isNilOrEmpty(campaignId);
    }),
    distinctUntilChanged()
  );

  orgSupport$ = this.getOrganizationDetails.watch().valueChanges
    .pipe(
      takeUntil(this.disposer),
      map(({ data }) => data.getOrganization.administratorUser)
    );

  constructor(
    private _cd: ChangeDetectorRef,
    private _appPersistantStateService: AppPersistantStateService,
    private _getCampaignSupportGQL: GetCampaignSupportGQL,
    private getOrganizationDetails: GetOrganizationDetailsGQL,
    private _route: ActivatedRoute,
    private _windowRef: WindowRef,
    public _dialogRef: MatDialogRef<SupportDialogComponent>
  ) {
    super();
  }

  close(data: any = null) {
    this._dialogRef.close(data);
  }

  ngOnInit() {
    this.campaignId$.pipe(
      switchMap<string, any>((campaignId) => {
        if (isNilOrEmpty(campaignId)) {
          return this.orgSupport$;
        }

        return this._getCampaignSupportGQL.fetch({ campaignId })
          .pipe(
            map(({ data }) => data.getCampaign.supportUser)
          );
      })
    )
      .subscribe(supportUser => {
        this.supportUser = supportUser as SupportUserType;
        this._cd.markForCheck();
      });
  }

  enableProdFeatureFlags() {
    this._appPersistantStateService.update(StoreNames.FeatureEnvironment, 'prod');
    this._cd.markForCheck();
  }

  disableProdFeatureFlags() {
    this._appPersistantStateService.update(StoreNames.FeatureEnvironment, '');
    this._cd.markForCheck();
  }

  isProdFeatureFlagEnabled() {
    return this._appPersistantStateService.getValue(StoreNames.FeatureEnvironment) === 'prod';
  }

}
